import type { SiteConfigFn, SiteVsmContext } from '@wix/thunderbolt-vsm-api-types'
import type { IRoutingConfig } from '@wix/thunderbolt-types'

export const getRouterSiteConfig = ({ api }: SiteVsmContext): IRoutingConfig => {
	const mainPageId = api.rendererModel.getMainPageId()
	const pagesMap = api.rendererModel.getPagesMap()
	const baseUrl = api.publicModel.getExternalBaseUrl()

	const routersConfig = api.rendererModel.getRouters()?.configMap || {}

	const mainPageRoute = {
		'./': {
			type: 'Static' as const,
			pageId: mainPageId,
		},
	}

	const dynamicPagesRoutes: Record<string, { type: 'Dynamic'; pageIds: string[] }> = {}
	for (const [_routerId, routerConfig] of Object.entries(routersConfig)) {
		const prefix = routerConfig.prefix
		const pages = routerConfig.pages || {}
		const pageIds = Object.values(pages)

		dynamicPagesRoutes[`./${prefix}`] = {
			type: 'Dynamic',
			pageIds,
		}
	}

	const pageIdToPrefix: Record<string, string> = {}
	for (const router of Object.values(routersConfig)) {
		const prefix = router.prefix
		const pages = router.pages || {}
		for (const pageId of Object.values(pages)) {
			pageIdToPrefix[pageId] = prefix
		}
	}

	const pageIdToPageRoute = api.rendererModel.getPagesRoute()

	const pagesRoutes: Record<string, { type: 'Static'; pageId: string }> = {}
	for (const [pageId, pageRoute] of Object.entries(pageIdToPageRoute)) {
		// Skip pages that have a router prefix
		if (pageId in pageIdToPrefix) {
			continue
		}

		const route = pageRoute.route
		pagesRoutes[route] = {
			type: 'Static',
			pageId: pageRoute.pageId,
		}
	}

	const routes = {
		...pagesRoutes,
		...dynamicPagesRoutes,
		...mainPageRoute,
	}

	const customNotFoundPageId = api.rendererModel.getCustomNotFoundPageId()
	const customNotFoundPage = customNotFoundPageId ? pagesMap[customNotFoundPageId] : undefined
	const customNotFoundPageData = customNotFoundPage
		? {
				customNotFoundPage: {
					pageId: customNotFoundPageId as string,
					pageRoute: `./${customNotFoundPage.pageUriSEO}`,
				},
			}
		: {}

	const siteInfo = api.rendererModel.getSiteInfo()
	const isWixSite = siteInfo?.documentType === 'WixSite'

	const siteMetaData = api.rendererModel.getSiteMetaData()
	const disableStaticPagesUrlHierarchy = siteMetaData?.seoData?.disableStaticPagesUrlHierarchy || false
	const isBuilderComponentModel = !!siteMetaData?.builderComponentModel

	const partialRouteMatchingAllowed = api.rendererModel.getPartialRouteMatchingAllowed()

	const config: IRoutingConfig = {
		baseUrl,
		mainPageId,
		pagesMap,
		disableStaticPagesUrlHierarchy,
		routes,
		pageIdToPrefix,
		isWixSite,
		isBuilderComponentModel,
		...customNotFoundPageData,
	}

	if (partialRouteMatchingAllowed !== undefined) {
		config.partialRouteMatchingAllowed = partialRouteMatchingAllowed
	}

	return config
}

const routerSiteConfig: SiteConfigFn<IRoutingConfig> = (params) => {
	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig: getRouterSiteConfig(params),
	}
}

const name = 'router'

export const config = {
	site: routerSiteConfig,
	reactRendererSite: routerSiteConfig,
	reactNativeSite: routerSiteConfig,
	name,
}
